import { useCallback, useMemo, useState } from "react";

import { Search as SearchIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import {
  DateRangePicker,
  SingleInputDateRangeField,
} from "@mui/x-date-pickers-pro";
import { PageContainer } from "@/features/ui";

import SearchSvg from "../assets/search.svg";

import { useAuth } from "@/features/authentication";
import { UserOutletsSelect } from "@/features/outlets";
import { apiOutletClient, formatNumber } from "@/features/shared";
import { CenteredLoading, useSnackbar } from "@/features/ui";

export const TrialBalance = () => {
  const { outlet, changeOutlet } = useAuth();
  const { showSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));

  const isExactlyMd = isMdUp && !isLgUp;

  const [dateRange, setDateRange] = useState([null, null]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);

  const columns = [
    {
      field: "openingBalanceDebit",
      headerName: "Saldo Awal (Debit)",
      type: "number",
      flex: 1.5,
      minWidth: 150,
      sortable: false,
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "openingBalanceCredit",
      headerName: "Saldo Awal (Kredit)",
      type: "number",
      flex: 1.5,
      minWidth: 150,
      sortable: false,
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "periodDebit",
      headerName: "Perubahan Debit",
      type: "number",
      flex: 1.5,
      minWidth: 150,
      sortable: false,
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "periodCredit",
      headerName: "Perubahan Kredit",
      type: "number",
      flex: 1.5,
      minWidth: 150,
      sortable: false,
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "endingBalanceDebit",
      headerName: "Saldo Akhir (Debit)",
      type: "number",
      flex: 1.5,
      minWidth: 150,
      sortable: false,
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "endingBalanceCredit",
      headerName: "Saldo Akhir (Kredit)",
      type: "number",
      flex: 1.5,
      minWidth: 150,
      sortable: false,
      valueFormatter: (value) => formatNumber(value),
    },
  ];

  const fetchData = useCallback(async () => {
    const [startDate, endDate] = dateRange;
    if (!outlet || !startDate || !endDate) return;

    setData([]);
    setLoading(true);
    setHasSearched(true);
    try {
      const {
        data: { data: responseData },
      } = await apiOutletClient.get(
        "chart-of-account-transactions/trial-balance",
        {
          headers: {
            "Outlet-Id": outlet.id,
          },
          params: {
            start_date: startDate.format("YYYY-MM-DD"),
            end_date: endDate.format("YYYY-MM-DD"),
          },
        }
      );
      setData(responseData);
    } catch (error) {
      console.error("Error fetching trial balance:", error);
      showSnackbar("Gagal memuat data neraca percobaan. Silakan coba lagi.");
    } finally {
      setLoading(false);
    }
  }, [dateRange, outlet, showSnackbar]);

  // Build tree paths from nested set model (lft/rgt)
  const dataWithPaths = useMemo(() => {
    if (!data.length) return [];

    // Sort by lft to ensure proper order
    const sortedRows = [...data].sort((a, b) => a.lft - b.lft);

    // Build paths for each row based on nested set model
    return sortedRows.map((row) => {
      const path = [];

      // Find all ancestors (nodes that contain this node)
      sortedRows.forEach((potentialAncestor) => {
        if (
          potentialAncestor.lft < row.lft &&
          potentialAncestor.rgt > row.rgt
        ) {
          path.push(`${potentialAncestor.code} - ${potentialAncestor.name}`);
        }
      });

      // Add current node to path with code
      path.push(`${row.code} - ${row.name}`);

      return {
        ...row,
        path: path,
      };
    });
  }, [data]);

  return (
    <PageContainer maxWidth="xl" breadcrumbs={[]}>
      <Stack spacing={3}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 5, md: 4, lg: 5 }}>
            <UserOutletsSelect value={outlet} onChange={changeOutlet} />
          </Grid>
          <Grid size={{ xs: 7, md: 6, lg: 5 }}>
            <DateRangePicker
              value={dateRange}
              disableFuture
              slots={{
                field: SingleInputDateRangeField,
              }}
              slotProps={{
                field: {
                  fullWidth: true,
                },
              }}
              format="DD/MMM/YYYY"
              localeText={{ start: "Dari", end: "Sampai" }}
              onChange={(newDateRange) => {
                setDateRange(newDateRange);
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2, lg: 2 }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={!isExactlyMd && <SearchIcon />}
              onClick={fetchData}
            >
              {isExactlyMd && <SearchIcon />}
              {!isExactlyMd && "Cari"}
            </Button>
          </Grid>
        </Grid>
        {!loading && !hasSearched && (
          <Stack
            sx={{ justifyContent: "center", alignItems: "center", pt: 10 }}
            spacing={2}
          >
            <Box component="img" src={SearchSvg} sx={{
              height: "20vh"
            }} />
            <Typography sx={{ fontSize: "1.5rem" }}>
              Silakan isi filter di atas untuk melihat laporan
            </Typography>
          </Stack>
        )}
        {loading && <CenteredLoading sx={{ height: "80vh" }} />}
        {!loading && hasSearched && (
          <DataGridPro
            rows={dataWithPaths}
            columns={columns}
            getRowId={(row) => row.code}
            getRowHeight={() => "auto"}
            treeData
            getTreeDataPath={(row) => row.path}
            defaultGroupingExpansionDepth={-1}
            groupingColDef={{
              headerName: "Akun Perkiraan",
              flex: 2,
            }}
            initialState={{
              columns: {
                columnVisibilityModel: {
                  periodDebit: false,
                  periodCredit: false,
                },
              },
            }}
            showToolbar
            disableRowSelectionOnClick
            hideFooterPagination
            hideFooterSelectedRowCount
          />
        )}
      </Stack>
    </PageContainer>
  );
};

export default TrialBalance;
